import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { logger } from "shared/logger/logger";
import { jwtDecode } from "jwt-decode";

export const AuthContext = createContext(null);

/* ==========================
   SECURITE — Stockage du token
   ========================== 
   Changement : localStorage → sessionStorage

   Comparaison :
     localStorage  : persiste après fermeture du navigateur, partagé entre
                     tous les onglets de la même origine. Un script XSS
                     injecté dans n'importe quelle page peut lire le token.
     sessionStorage: limité à l'onglet courant et effacé à la fermeture.
                     Réduit la fenêtre d'exploitation XSS (pas d'accès
                     cross-tab, durée de vie plus courte).

   Note : la solution idéale reste les cookies HttpOnly + Secure + SameSite
   gérés par le backend (inaccessibles depuis JavaScript). Cette migration
   sessionStorage est une amélioration intermédiaire réalisable côté frontend
   sans modifier le backend.

   Impact sur les tests : les tests qui utilisaient localStorage.setItem/clear
   ont été mis à jour pour utiliser sessionStorage.
========================== */
const TOKEN_KEY = "access_token";

const tokenStorage = {
  get:    ()      => sessionStorage.getItem(TOKEN_KEY),
  set:    (token) => sessionStorage.setItem(TOKEN_KEY, token),
  remove: ()      => sessionStorage.removeItem(TOKEN_KEY),
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  /* ==========================
     Utils
  ========================== */

  const getToken = useCallback(() => tokenStorage.get(), []);

  const clearSession = useCallback(() => {
    tokenStorage.remove();
    setUser(null);
  }, []);

  const decodeUserFromToken = useCallback(() => {
    const token = getToken();
    if (!token) return null;

    try {
      const decoded = jwtDecode(token);

      if (decoded.exp && Date.now() >= decoded.exp * 1000) {
        logger.warn("Token expiré", { userId: decoded.user_id });
        clearSession();
        return null;
      }

      return {
        tenant: decoded.tenant_id,
        id:     decoded.user_id ?? decoded.sub,
        username:    decoded.username ?? decoded.name,
        roles:       decoded.roles ?? [],
        permissions: decoded.permissions ?? {},
        exp:         decoded.exp,
      };
    } catch (err) {
      logger.error(err, { feature: "auth", action: "decodeUserFromToken" });
      clearSession();
      return null;
    }
  }, [getToken, clearSession]);

  /* ==========================
     Auth actions
  ========================== */

  const login = useCallback(
    (token) => {
      tokenStorage.set(token);
      setUser(decodeUserFromToken());
    },
    [decodeUserFromToken]
  );

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  /* ==========================
     Initial load
  ========================== */

  useEffect(() => {
    setUser(decodeUserFromToken());
    setLoading(false);
  }, [decodeUserFromToken]);

  /* ==========================
     Auto logout (exp)
  ========================== */

  useEffect(() => {
    if (!user?.exp) return;

    const expiresIn = user.exp * 1000 - Date.now();

    if (expiresIn <= 0) {
      logout();
      return;
    }

    const timer = setTimeout(() => {
      logger.warn("Déconnexion automatique (token expiré)");
      logout();
    }, expiresIn);

    return () => clearTimeout(timer);
  }, [user?.exp, logout]);

  /* ==========================
     Multi-tabs sync
     Note : sessionStorage n'émet PAS d'événement storage cross-tabs
     (c'est voulu : chaque onglet a sa propre session).
     On conserve l'écoute pour la compatibilité si on revient à
     localStorage plus tard, mais elle est inopérante avec sessionStorage.
  ========================== */

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== TOKEN_KEY) return;
      setUser(decodeUserFromToken());
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [decodeUserFromToken]);

  /* ==========================
     Auto-logout on 401
  ========================== */

  useEffect(() => {
    const handleUnauthorized = () => {
      logger.warn("Session expirée — déconnexion automatique (401)");
      clearSession();
    };

    window.addEventListener("unauthorized", handleUnauthorized);
    return () => window.removeEventListener("unauthorized", handleUnauthorized);
  }, [clearSession]);

  /* ==========================
     Permissions
  ========================== */

  const hasPermission = useCallback(
    (pageKey, action) => {
      const pagePermissions = user?.permissions?.[pageKey];
      return (
        Array.isArray(pagePermissions) &&
        pagePermissions?.includes(action)
      );
    },
    [user]
  );

  /* ==========================
     Context value
  ========================== */

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      login,
      logout,
      hasPermission,
    }),
    [user, loading, login, logout, hasPermission]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
